import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BookingDropdown from "./BookingDropdown";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-stables-600/95 backdrop-blur-sm shadow-lg"
          : "bg-cream-50/10 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/logoTransparentLighter.png"
                alt="The Stables Logo"
                className="h-12 w-auto"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              About
            </Link>
            <Link
              to="/horse-rides"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Horse Rides
            </Link>
            <Link
              to="/horse-ride-picnic"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Picnic Ride
            </Link>
            <Link
              to="/parties"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Parties
            </Link>
            <Link
              to="/photoshoots"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Photoshoots
            </Link>
            <Link
              to="/proposals"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Proposals
            </Link>
            <Link
              to="/faq"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              className="text-white hover:text-stables-200 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
            >
              Contact
            </Link>
            <div className="ml-4">
              <BookingDropdown
                triggerLabel="Book Now"
                align="right"
                triggerClassName="bg-earth-500 hover:bg-earth-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-earth-600/30"
              />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-stables-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-stables-500 transition-colors duration-300"
            >
              <span className="sr-only">Open main menu</span>
              {!isOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div
            className={`px-2 pt-2 pb-3 space-y-1 sm:px-3 backdrop-blur-sm border-t border-white/20 transition-all duration-300 ${
              isScrolled ? "bg-stables-600/95" : "bg-black/20"
            }`}
          >
            <Link to="/" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Home</Link>
            <Link to="/about" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">About Us</Link>
            <Link to="/horse-rides" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Horse Rides</Link>
            <Link to="/horse-ride-picnic" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Horse Ride & Picnic</Link>
            <Link to="/parties" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Parties</Link>
            <Link to="/photoshoots" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Photoshoots</Link>
            <Link to="/proposals" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Proposals</Link>
            <Link to="/faq" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">FAQ</Link>
            <Link to="/contact" onClick={() => setIsOpen(false)} className="text-white hover:text-stables-200 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300">Contact Us</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
